/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, Search, ExternalLink, Pencil, Check, X, Loader2, AlertTriangle, RotateCcw, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';

/**
 * Pricing Rules: every rule Shippy applies to a price, wherever it lives
 * (surcharge rules, per-shipment fees, fuel, lane extras, carrier and country
 * settings, zones, pickup profiles, fuel indexes, config constants), in one
 * searchable list. Each row says where to edit it; config constants are
 * edited in place (an override replaces the code value on every worker until
 * it is reset). Everything comes from api/pricing-rules/.
 */

const KIND_STYLE = {
  surcharge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  shipment_fee: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  refusal: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  fuel: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  lane_extra: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  carrier: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  country: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  zone: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  pickup: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  fuel_index: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  config: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  upright: 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300',
};

const dstr = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

function KindBadge({ kind, kinds }) {
  const label = (kinds.find(([k]) => k === kind) || [kind, kind])[1];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${KIND_STYLE[kind] || 'bg-muted'}`}>{label}</span>;
}

/** Editor for one config constant. Values travel as JSON: numbers, booleans, lists of ISO2 codes, {ISO2: number} maps. */
function ConfigEditor({ cfg, onSaved, onCancel }) {
  const kind = cfg.kind;
  const initial = () => {
    const v = cfg.value;
    if (kind === 'set' || kind === 'list') return (v || []).join(', ');
    if (kind === 'map') return JSON.stringify(v || {}, null, 1);
    if (kind === 'bool') return !!v;
    return v === null || v === undefined ? '' : String(v);
  };
  const [val, setVal] = useState(initial);
  const [note, setNote] = useState(cfg.override?.note || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const payload = () => {
    if (kind === 'bool') return !!val;
    if (kind === 'set' || kind === 'list') return String(val);
    if (kind === 'map') return JSON.parse(val || '{}');
    if (kind === 'float_or_null' && String(val).trim() === '') return null;
    if (kind === 'str') return String(val);
    return Number(val);
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      let value;
      try { value = payload(); } catch { setErr('Not valid JSON'); setBusy(false); return; }
      if (cfg.caution && !window.confirm(`${cfg.caution}\n\nApply ${cfg.key} = ${JSON.stringify(value)} on every surface now?`)) { setBusy(false); return; }
      await axiosInstance.put('api/pricing-rules/config/', { key: cfg.key, value, note });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Could not save');
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-md border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 p-3 space-y-2">
      <div className="text-xs text-muted-foreground">{cfg.unit && <span className="font-medium text-foreground">{cfg.unit}</span>} {cfg.unit ? '· ' : ''}code default: <code className="bg-muted px-1 rounded">{JSON.stringify(cfg.code_default)}</code></div>
      {kind === 'bool' ? (
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!val} onChange={(e) => setVal(e.target.checked)} /> {val ? 'on' : 'off'}</label>
      ) : kind === 'map' ? (
        <textarea className="w-full h-40 rounded-md border border-input bg-background px-2 py-1 text-xs font-mono" value={val} onChange={(e) => setVal(e.target.value)} />
      ) : kind === 'str' && cfg.choices ? (
        <select className="flex h-9 rounded-md border border-input bg-background px-3 text-sm" value={val} onChange={(e) => setVal(e.target.value)}>
          {cfg.choices.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      ) : (
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={kind === 'set' || kind === 'list' ? 'ES, PT, IT' : ''} className="max-w-xl" />
      )}
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (decision, date, who): kept with the value" className="max-w-xl" />
      {err && <div className="text-xs text-red-600 dark:text-red-400">{err}</div>}
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={busy}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />} Save</Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
      </div>
    </div>
  );
}

export default function PricingRules() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [carrier, setCarrier] = useState('');
  const [country, setCountry] = useState('');
  const [editing, setEditing] = useState(null);     // item id being edited (config)
  const [open, setOpen] = useState({});              // expanded descriptions
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axiosInstance.get('api/pricing-rules/');
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load the rules');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const kinds = data?.kinds || [];
  const carriers = useMemo(() => [...new Map(items.filter(i => i.carrier).map(i => [i.carrier.id, i.carrier.name])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [items]);
  const countries = useMemo(() => [...new Set(items.flatMap(i => i.countries || []))].sort(), [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter(i => {
      if (kind && i.kind !== kind) return false;
      if (carrier && String(i.carrier?.id) !== carrier) return false;
      if (country && !(i.countries || []).includes(country)) return false;
      if (!needle) return true;
      const hay = [i.title, i.key, i.value, i.scope, i.note, i.description, i.source, i.condition, i.carrier?.name,
        (i.countries || []).join(' '), (i.flags || []).join(' ')].filter(Boolean).join(' ').toLowerCase();
      return needle.split(/\s+/).every(w => hay.includes(w));
    });
  }, [items, q, kind, carrier, country]);

  const resetOverride = async (it) => {
    if (!window.confirm(`Put the code default back for ${it.key}?\n\n${JSON.stringify(it.config.code_default)}`)) return;
    setResetting(it.id);
    try { await axiosInstance.delete('api/pricing-rules/config/', { params: { key: it.key } }); await load(); }
    catch (e) { alert(e?.response?.data?.error || 'Could not reset'); }
    finally { setResetting(null); }
  };

  if (loading && !data) return <ShippyLoader />;

  return (
    <div className="px-6 py-8 max-w-none">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><SlidersHorizontal className="h-7 w-7 text-primary" /> Pricing Rules</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every rule that touches a price, in one place: surcharges, per-shipment fees, fuel, lane extras, carrier and country settings, zones, pickup, and the config constants. Each row says where it is edited; config values are edited here.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Refresh</Button>
      </div>
      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</div>}
      {data?.errors && Object.keys(data.errors).length > 0 && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Some sources could not be read: {Object.entries(data.errors).map(([k, v]) => `${k}: ${v}`).join(' · ')}
        </div>
      )}

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: carrier, country, label, amount, note, invoice..." className="pl-8 w-80" />
        </div>
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
          <option value="">All carriers</option>
          {carriers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={country} onChange={(e) => setCountry(e.target.value)}>
          <option value="">All countries</option>
          {countries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(q || kind || carrier || country) && (
          <Button variant="ghost" size="sm" onClick={() => { setQ(''); setKind(''); setCarrier(''); setCountry(''); }}><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>
        )}
      </div>
      <div className="mb-4 flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setKind('')} className={`rounded-full border px-2.5 py-0.5 text-xs ${!kind ? 'border-primary bg-primary/10 font-medium' : 'border-border hover:bg-muted'}`}>All {items.length}</button>
        {kinds.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setKind(kind === k ? '' : k)}
            className={`rounded-full border px-2.5 py-0.5 text-xs ${kind === k ? 'border-primary bg-primary/10 font-medium' : 'border-border hover:bg-muted'}`}>
            {label} {data?.counts?.[k] || 0}
          </button>
        ))}
      </div>

      <div className="text-xs text-muted-foreground mb-2">{filtered.length} of {items.length} rules</div>
      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Rule</th>
              <th className="px-3 py-2">Scope</th>
              <th className="px-3 py-2">Value</th>
              <th className="px-3 py-2">Since</th>
              <th className="px-3 py-2">Why / note</th>
              <th className="px-3 py-2">Edit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(it => {
              const isConfig = it.kind === 'config';
              const isOpen = !!open[it.id];
              return (
                <React.Fragment key={it.id}>
                  <tr className={`align-top ${!it.active ? 'opacity-50' : ''} ${it.window?.status === 'expired' ? 'opacity-60' : ''}`}>
                    <td className="px-3 py-2"><KindBadge kind={it.kind} kinds={kinds} /></td>
                    <td className="px-3 py-2">
                      <div className="font-medium flex items-center gap-1.5">
                        {isConfig && it.description ? (
                          <button type="button" onClick={() => setOpen(prev => ({ ...prev, [it.id]: !prev[it.id] }))} className="inline-flex items-center gap-1 text-left">
                            {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                            {it.title}
                          </button>
                        ) : it.title}
                      </div>
                      {it.carrier && <div className="text-xs text-muted-foreground">{it.carrier.name}</div>}
                      {isConfig && <div className="text-[11px] font-mono text-muted-foreground">{it.key}</div>}
                      {it.condition && <div className="text-xs text-muted-foreground">{it.condition}</div>}
                      {(it.flags || []).length > 0 && (
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {it.flags.map(f => <span key={f} className={`rounded px-1.5 py-0 text-[10px] ${f === 'overridden' ? 'bg-indigo-600 text-white' : 'bg-muted text-muted-foreground'}`}>{f}</span>)}
                        </div>
                      )}
                      {it.window && (
                        <div className={`text-[11px] mt-0.5 ${it.window.status === 'active' ? 'text-emerald-700 dark:text-emerald-400' : it.window.status === 'scheduled' ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>
                          {it.window.status}: {it.window.from || '...'} to {it.window.until || '...'}
                        </div>
                      )}
                      {!it.active && <div className="text-[11px] text-muted-foreground">inactive</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {(it.countries || []).length > 0 && (it.countries.length <= 8
                        ? <div className="font-mono">{it.countries.join(' ')}</div>
                        : <div className="font-mono" title={it.countries.join(', ')}>{it.countries.slice(0, 8).join(' ')} +{it.countries.length - 8}</div>)}
                      <div className="text-muted-foreground">{it.scope}</div>
                    </td>
                    <td className="px-3 py-2 font-medium max-w-md break-words">
                      {it.value}
                      {editing === it.id && it.config && (
                        <ConfigEditor cfg={it.config} onSaved={async () => { setEditing(null); await load(); }} onCancel={() => setEditing(null)} />
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{dstr(it.since)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-sm">
                      {it.note}
                      {it.source && <div className="text-[10px] opacity-70 mt-0.5">{it.source}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {isConfig ? (
                        it.config?.editable ? (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" onClick={() => setEditing(editing === it.id ? null : it.id)} title="Override the code value on every surface"><Pencil className="h-3.5 w-3.5" /></Button>
                            {it.config.override && (
                              <Button size="sm" variant="ghost" onClick={() => resetOverride(it)} disabled={resetting === it.id} title="Back to the code default">
                                {resetting === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                              </Button>
                            )}
                          </div>
                        ) : <span className="text-[11px] text-muted-foreground" title="Changed in code and deployed; ask for it in #shippy">code only</span>
                      ) : it.edit?.path ? (
                        <Button size="sm" variant="outline" onClick={() => navigate(it.edit.path)}>{it.edit.label} <ExternalLink className="h-3 w-3 ml-1" /></Button>
                      ) : null}
                    </td>
                  </tr>
                  {isConfig && isOpen && it.description && (
                    <tr className="bg-muted/20">
                      <td />
                      <td colSpan={6} className="px-3 pb-3 pt-0 text-xs text-muted-foreground">
                        <div className="flex items-start gap-1.5"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span>{it.description}{it.config?.line ? <span className="opacity-60"> (packing_config.py line {it.config.line})</span> : null}</span></div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">Nothing matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        Rate tables themselves (weight, volumetric, pallet cards, zone rows) live in Shipping Config; this page lists the rules applied on top of them. A config override applies on every worker within seconds and resets to the code default when removed.
      </p>
    </div>
  );
}
